/**
 * Which interval a PIP shows, and the one rule it can never break.
 *
 * A PIP is a *higher timeframe*. That is the whole idea: a weekly in the corner
 * of a daily. So the one invariant is that its interval is **strictly coarser
 * than the chart it sits in** — a weekly PIP on a weekly chart is two copies of
 * the same picture, drawn twice and read once.
 *
 * That invariant is the reason this module exists rather than a constant. The
 * chart's own interval changes underneath the PIP: someone on a daily chart with
 * a weekly PIP switches to weekly, and the stored `1w` is now equal to the
 * chart's. Nothing about storing an interval can prevent that, so the resolution
 * happens on read — {@link pipFrequencyFor} is what the view calls, and the
 * stored value is a preference rather than an answer.
 *
 * ## The ladder is served intervals, not every interval
 *
 * `qv-data` holds one daily file per symbol and derives everything coarser from
 * it (`qf/resample.py`). So a PIP's ladder starts at a week and the rungs are
 * `1w`, `1m`, `3m`, `12m` — built from the daily file, and nothing that would
 * come back as a failed chart group. Intraday bars are served since 2026-09-27
 * (`docs/plans/intraday.md`), but a PIP is a glance at the timeframe *above*,
 * and an intraday chart's timeframe above is a day or more.
 *
 * `3m` and `12m` are a quarter and a year, expressible because
 * `ChartGroup.frequency` is a pattern rather than an enum and the server folds
 * *count* consecutive resampled bars. They are rungs rather than a separate
 * concept.
 *
 * ## Comparison is by duration, not by unit
 *
 * `nextCoarser("10d")` is `1m`, not `1w`, because ten days is longer than a
 * week. Stepping the *unit* and keeping the count is the obvious implementation
 * and is wrong for every count above one — it would put a `1w` PIP on a `10d`
 * chart, which is a finer timeframe in the corner of a coarser one.
 *
 * Pure and tested here rather than inlined in the component, for the reason
 * every `lib/` module in this app gives: anything that reaches the chart is out
 * of jsdom's reach, and this is the part with the arithmetic in it.
 */

import type { ChartGroup, Frequency } from "@qufolio/model";
import { splitInterval, type IntervalUnit } from "./interval-mru";
import { available } from "./features";
import type { FeatureId } from "./brand-registry";
import { marketForSymbol } from "./market";
import { isComposite } from "./composite";

/**
 * The three PIP enums, derived from the contract rather than restated.
 *
 * `NonNullable` because each field is optional there and absence is meaningful
 * — `pipView` missing is what "no PIP" *is*. Restating the unions here would be
 * a second source of truth that codegen cannot update, which is the one thing
 * the contract rule exists to prevent.
 */
export type PipView = NonNullable<ChartGroup["pipView"]>;
export type PipCorner = NonNullable<ChartGroup["pipCorner"]>;

/**
 * The views the **data panel** has tabs for — every PIP view except `chart`.
 *
 * A tab holding a second chart is a second chart, not a view of this symbol, so
 * the exclusion is a rule rather than an omission and the type says it. Anything
 * accepting a tab from outside — an agent, a command search — narrows to this
 * before it can call.
 */
export type DataTab =
  | Exclude<PipView, "chart"> | "barIntel" | "holdings" | "congress" | "seasonality" | "etfComponents" | "etfHolders";

/**
 * Whether a view is one the data panel can show.
 *
 * `barIntel` is a data-panel tab and **not** a PIP view: it describes the chart
 * beside it, measured against that chart's own bars, so a PIP showing it would
 * be a second chart reading the first one's numbers. Kept out of `PipView` (a
 * contract enum, saved on every chart group) for that reason.
 */
export function isDataTab(view: PipView): view is Exclude<PipView, "chart"> {
  return view !== "chart";
}
export type PipSize = NonNullable<ChartGroup["pipSize"]>;

/**
 * Two tabs' names, here so the data panel (`components/symbol-views.tsx`) and the
 * command search's "Open the data panel — Fin St" row print the same words —
 * the reasons for each name are on their constants there.
 */
export const FIN_ST_LABEL = "Fin St";
export const AI_REVIEW_LABEL = "AI Review";

/**
 * The views, in the order the chip cycles and a menu lists them.
 *
 * `chart` leads because it is what a PIP defaults to and what the feature was
 * asked for; the rest follow the data panel's tab order so the two never
 * disagree about where Peers sits.
 */
export const PIP_VIEWS: readonly PipView[] = [
  "chart",
  "profile",
  "peers",
  "fundamentals",
  "summary",
  "news",
];

/**
 * Views with no source outside the US.
 *
 * News and sentiment are vendor feeds held only for US symbols. Both render
 * perfectly for `AAPL`, so this is not the same thing as a view being unbuilt —
 * that is about the view, and this is about the symbol under it.
 *
 * Here rather than beside the data panel's registry because **two surfaces share
 * the rule** and only one of them may import React: this module is pure, the
 * registry is a file full of components, and a second copy of the list is how
 * the PIP ends up offering a chip the panel has already hidden. See the
 * re-enable item in `docs/TODO.md` — deleting a name is the whole of turning one
 * back on.
 */
export const US_ONLY_VIEWS: ReadonlySet<PipView> = new Set(["news", "sentiment"]);

/**
 * Views that are a brand feature, and which one.
 *
 * A *second* reason a view can be absent, and deliberately in the same function
 * as the first: the panel's tab strip and the PIP's chips both read this, and a
 * gate checked in only one of them is how the PIP ends up offering a chip the
 * panel has already hidden — which is the whole argument `US_ONLY_VIEWS` makes
 * one comment above.
 *
 * **This carries the capability too, through the registry.** It used to be a
 * `VIEW_CAPABILITY` map holding `summary: "feat:analyst"` directly; the feature
 * registry now records that pairing (`lib/brand-registry.ts`), and `available`
 * spends it. Two maps meant `can("feat:analyst")` was asked twice per call and
 * the pairing lived in two files — the second of which nobody would think to
 * update when a capability was renamed.
 *
 * So the order is brand, then plan: an app that does not have the analyst never
 * asks whether this account may use one.
 *
 * **Hidden rather than dimmed**, which is `journalEnabled()`'s call and not the
 * indicator picker's. The picker is a shop window and a locked row there is an
 * advertisement; a tab strip is navigation, and a tab that refuses on arrival is
 * a dead end with a click spent on it. See `docs/features/entitlements.md`.
 *
 * Permissive while `/auth/me` is in flight, like everything in
 * `lib/entitlements.ts`. The server refuses regardless — `/analyst/summary`
 * carries `require("feat:analyst", "lim:analystCallsDay")`.
 *
 * The rest are not brandable: a profile, peers, news and sentiment are what a
 * symbol *is*, not a product somebody chose to sell.
 */
const VIEW_FEATURE: Partial<Record<PipView, FeatureId>> = {
  fundamentals: "fundamentals",
  summary: "analyst",
};

/** Whether `view` has a source for this symbol's market, and a plan behind it. */
export function viewAvailableFor(view: PipView, symbol: string): boolean {
  // `available` is both gates, brand first — see `lib/features.ts`.
  const feature = VIEW_FEATURE[view];
  if (feature !== undefined && !available(feature)) return false;
  return !US_ONLY_VIEWS.has(view) || marketForSymbol(symbol).code === "US";
}

/**
 * The chips a PIP may offer for one symbol.
 *
 * `chart` always survives, which is what makes the fallback safe: a PIP whose
 * stored view has no source here shows the candles rather than nothing.
 */
export function pipViewsFor(symbol: string): readonly PipView[] {
  return PIP_VIEWS.filter((v) => viewAvailableFor(v, symbol));
}

/**
 * Whether the **data panel** offers `tab` for this symbol.
 *
 * The PIP views answer through `viewAvailableFor`. The two data-panel-only tabs
 * answer here, in this pure module for that function's reason — one rule, read
 * by every surface that lists tabs:
 *
 * - `barIntel` describes the chart beside it, so it is always offered.
 * - `holdings` is 13F, which only US-listed securities are reported in, and is
 *   **hidden** (not dimmed — the `US_ONLY_VIEWS` call) for anything else. A
 *   ratio such as `AAPL/SPY` is not a security anybody holds, so it is hidden
 *   there too. Not a `PipView`: that enum is contract data saved on every chart
 *   group, and a 13F table is no PIP view. `docs/plans/holdings-13f.md`.
 * - `etfComponents` is offered **only on an ETF** and `etfHolders` **only on
 *   anything else**, both US-listed and never a ratio. `etf` is the caller's
 *   answer from the symbol index, because the symbol alone cannot say.
 *   `docs/plans/etf-components.md`.
 */
export function dataTabAvailableFor(tab: DataTab, symbol: string, etf = false): boolean {
  if (tab === "barIntel") return true;
  if (tab === "etfComponents") return etf && marketForSymbol(symbol).code === "US" && !isComposite(symbol);
  if (tab === "etfHolders") return !etf && marketForSymbol(symbol).code === "US" && !isComposite(symbol);
  if (tab === "holdings") return marketForSymbol(symbol).code === "US" && !isComposite(symbol);
  // Senate and House trades: US stocks only, and nobody files a ratio.
  if (tab === "congress") return marketForSymbol(symbol).code === "US" && !isComposite(symbol);
  // Seasonality reads the yearly store, which has symbols, not ratios. Who may
  // see it (Pro) is the host's question — `symbolViewsFor` — not the symbol's.
  if (tab === "seasonality") return !isComposite(symbol);
  return viewAvailableFor(tab, symbol);
}

/** The sizes, smallest first. */
export const PIP_SIZES: readonly PipSize[] = ["s", "m", "l"];

/**
 * The corners the automatic rule chooses between, plus `auto` itself.
 *
 * The two right-hand corners are storable — a user overriding the choice may
 * want one — but they are not offered in the cycle: the right edge is the price
 * axis, top-right carries the feed badge and error column, and bottom-right the
 * chart's own controls. Offering a corner that lands on furniture is offering a
 * mistake.
 */
export const PIP_CORNERS: readonly PipCorner[] = ["auto", "topLeft", "bottomLeft"];

/**
 * How much of the price pane the PIP fills, per size.
 *
 * Fractions rather than pixels, because the pane's size is not something the
 * user states: the same panel has to stay legible in a full-width chart and in
 * one half of a two-chart layout. The renderer clamps the result, so a very
 * small pane gets a floor rather than an unreadable sliver.
 */
export const PIP_FRACTIONS: Record<PipSize, number> = { s: 0.2, m: 0.26, l: 0.34 };

/**
 * Seconds in one unit, for ordering two intervals.
 *
 * A month is the average Gregorian month and a year follows from it. Both are
 * approximations and neither needs to be anything else: this table orders
 * intervals, it does not date bars. The only property that matters is that no
 * two distinct rungs compare equal, and a calendar month is nowhere near four
 * weeks.
 */
const SECONDS: Record<IntervalUnit, number> = {
  s: 1,
  mi: 60,
  h: 3_600,
  d: 86_400,
  w: 604_800,
  m: 2_629_746,
};

/** An interval as a duration in seconds, for comparison only. */
export function intervalSeconds(frequency: Frequency): number {
  const { count, unit } = splitInterval(frequency);
  return count * SECONDS[unit];
}

/**
 * The rungs a PIP may stand on, coarsest last.
 *
 * Deliberately short. A PIP is a glance at the timeframe above, not a second
 * interval picker — offering `2w` and `6m` would make the chip a menu of
 * fourteen things nobody scans.
 */
export const PIP_LADDER: readonly Frequency[] = ["1w", "1m", "3m", "12m"];

/** Whether `candidate` is a longer bar than `chart`. */
export function isCoarser(candidate: Frequency, chart: Frequency): boolean {
  return intervalSeconds(candidate) > intervalSeconds(chart);
}

/**
 * The smallest rung strictly coarser than `frequency`, or `null` at the top.
 *
 * `null` rather than clamping to `12m`: a yearly chart has no higher timeframe
 * to show, and returning the same interval would draw the chart twice. The
 * caller turns that into a disabled control, which is the honest answer.
 */
export function nextCoarser(frequency: Frequency): Frequency | null {
  return PIP_LADDER.find((rung) => isCoarser(rung, frequency)) ?? null;
}

/**
 * The interval a PIP should actually request, given what was stored.
 *
 * The stored value wins **only while it is still coarser**. This is the read
 * that keeps the invariant: a `1w` preference on a chart that has since become
 * weekly resolves to `1m` rather than drawing the chart again, and the stored
 * value is left alone so dropping the chart back to daily restores the weekly
 * the user picked.
 *
 * `null` means there is no valid PIP interval at all — the chart is already at
 * the top of the ladder.
 */
export function pipFrequencyFor(
  chart: Frequency,
  stored: Frequency | undefined,
): Frequency | null {
  if (stored && isCoarser(stored, chart)) return stored;
  return nextCoarser(chart);
}

/**
 * What the ⇄ button does: the chart takes the PIP's interval, the PIP takes the
 * next rung above that.
 *
 * Not a straight exchange, which is the obvious reading of the glyph and would
 * break the invariant immediately — putting the chart's finer interval into the
 * PIP is exactly the state this module exists to prevent. Promoting the PIP is
 * what a person means by the gesture: "make *that* the chart".
 *
 * Returns `null` when the promoted interval has nothing above it, and the caller
 * declines the swap rather than performing half of it.
 */
export function swapWithChart(
  chart: Frequency,
  pip: Frequency,
): { chart: Frequency; pip: Frequency } | null {
  // The guard is not defensive padding: `pipFrequencyFor` resolves the pair on
  // every read, so a caller holding a stale `pip` from before an interval change
  // can reach here with one that is no longer coarser. Promoting *that* would
  // move the chart to a finer interval than it already has, which reads as the
  // button doing the opposite of its glyph.
  if (!isCoarser(pip, chart)) return null;
  const above = nextCoarser(pip);
  if (!above) return null;
  return { chart: pip, pip: above };
}

/**
 * One edit to a chart's PIP, as a patch.
 *
 * A patch rather than four props threaded through the grid, and the same shape
 * `IndicatorPicker`'s `onPlot` already uses: the caller means "this key, that
 * value", and a change usually carries exactly one of them. `null` is a real
 * value here and not an absent one — `view: null` closes the panel, and
 * `frequency: null` unpins the interval so it follows the chart again.
 */
export interface PipPatch {
  view?: PipView | null;
  frequency?: Frequency | null;
  corner?: PipCorner;
  size?: PipSize;
  /**
   * The ⇄ button: promote the PIP's interval to the chart's.
   *
   * A flag rather than the two intervals, because it is **two** mutations — the
   * chart's frequency and the panel's — and the caller that owns the dispatch
   * vocabulary should make both or neither. {@link swapWithChart} decides what
   * they are; this only says the button was pressed.
   */
  swap?: boolean;
}

/** The most charts a layout may have and still offer a PIP. */
export const PIP_MAX_CHARTS = 2;

/**
 * Whether a PIP may be *offered* on a layout of this many charts.
 *
 * Past two, a cell is small enough that a panel covering a corner costs more
 * than it shows, and there is no corner left that is reliably empty. The key is
 * disabled rather than hidden, with the reason on hover — and a stored PIP is
 * **kept**, so dropping back to two restores it rather than making the user
 * set it up again.
 */
export function pipOfferedOn(chartCount: number): boolean {
  return chartCount > 0 && chartCount <= PIP_MAX_CHARTS;
}

/**
 * Whether this chart should *draw* its PIP right now.
 *
 * On two charts the panel hosts on the one you are **not** using, and swaps when
 * you click the other. The panel gets out of the way of whatever was just
 * reached for, and the chart you left keeps showing its higher timeframe — so
 * the layout is answering two questions at once rather than covering the one
 * being read.
 *
 * Visibility only. Both charts keep their subscriptions, because suppressing a
 * *request* would make clicking between charts cost a refetch each way — the
 * trade this codebase makes everywhere: never turn a repaint into a reload.
 *
 * With one chart there is nothing to be inactive against, so it always draws.
 * With three or more the panel is not offered at all.
 */
export function pipDrawnOn(
  chartGroupId: string,
  chartIds: readonly string[],
  activeChartId: string | undefined,
): boolean {
  if (!pipOfferedOn(chartIds.length)) return false;
  if (chartIds.length === 1) return true;
  // An `activeChartId` naming no chart on this layout — session state outliving
  // what it named, which `active-section.ts` documents three ordinary ways of —
  // would otherwise hide neither panel and show both. Falling back to the first
  // chart keeps exactly one on screen, which is the rule.
  const active = activeChartId && chartIds.includes(activeChartId) ? activeChartId : chartIds[0];
  return chartGroupId !== active;
}
